import {
  arrayToRecord,
  clean,
  newId,
  recordToArray,
  recursiveDeepCopy
} from "../chunk-QBFP6NZ3.js";
import {
  DateCodec,
  Entity,
  Id,
  Name,
  Url,
  fromRefinement,
  fromStringEnum,
  throwOnFail
} from "../chunk-ZGKVIETW.js";
import "../chunk-CDKS5KAL.js";

// src/presentation/slide.ts
import * as t2 from "io-ts";

// src/presentation/fractional-index.ts
import * as Index from "@slidebean/fractional-index";
function isFractionalIndex(input) {
  return Array.isArray(input) && Number.isInteger(input[0]) && Number.isInteger(input[1]) && Index.isValid(input);
}
var FractionalIndexCodec = fromRefinement(
  "FractionalIndex",
  isFractionalIndex
);
function hasDuplicatedIndexes(xs) {
  const indexes = xs.map(
    (x) => x?.fractionalIndex ? Index.toString(x.fractionalIndex) : ""
  );
  return new Set(indexes).size !== indexes.length;
}
function compareIndexes(left, right) {
  const leftExists = !!left;
  const rightExists = !!right;
  if (!leftExists && !rightExists) return 0;
  if (!leftExists) return -1;
  if (!rightExists) return 1;
  return Index.compare(left.fractionalIndex, right.fractionalIndex);
}
function compareIndexesAndIds(left, right) {
  const result = compareIndexes(left, right);
  if (result !== 0 || !left) return result;
  return (left.id ?? "").localeCompare(right.id ?? "");
}
function sortByIndex(xs) {
  const shouldDoSimpleSort = xs.every((x) => Index.isValid(x.fractionalIndex)) && !hasDuplicatedIndexes(xs);
  return shouldDoSimpleSort ? xs.slice().sort(compareIndexes) : Index.generate(xs.length).map((fractionalIndex, i) => ({
    ...xs[i],
    fractionalIndex
  }));
}

// src/presentation/slide-element/slide-element.ts
import * as t from "io-ts";
var SlideElementStyle = t.partial({
  x: t.number,
  y: t.number,
  z: t.number,
  w: t.number,
  h: t.number
});
var SlideElementType = /* @__PURE__ */ ((SlideElementType2) => {
  SlideElementType2["Background"] = "background";
  SlideElementType2["Cover"] = "cover";
  SlideElementType2["Heading"] = "heading";
  SlideElementType2["Footer"] = "footer";
  SlideElementType2["Paragraph"] = "paragraph";
  SlideElementType2["Bullets"] = "bullets";
  SlideElementType2["Quote"] = "quote";
  SlideElementType2["Image"] = "image";
  SlideElementType2["Number"] = "number";
  SlideElementType2["ChartBar"] = "chart-bar";
  SlideElementType2["ChartLine"] = "chart-line";
  SlideElementType2["ChartPie"] = "chart-pie";
  SlideElementType2["Table"] = "table";
  SlideElementType2["GoogleDoc"] = "google-doc";
  SlideElementType2["Code"] = "code";
  SlideElementType2["Timeline"] = "timeline";
  SlideElementType2["Funnel"] = "funnel";
  SlideElementType2["Venn"] = "venn";
  SlideElementType2["Video"] = "video";
  SlideElementType2["Audio"] = "audio";
  SlideElementType2["Line"] = "line";
  SlideElementType2["Circle"] = "circle";
  SlideElementType2["Square"] = "square";
  return SlideElementType2;
})(SlideElementType || {});
var SlideElementTypeCodec = fromStringEnum(
  "SlideElementType",
  SlideElementType
);
var SlideElement = t.type({
  id: Id,
  type: SlideElementTypeCodec,
  data: t.any,
  options: t.any,
  style: SlideElementStyle,
  fractionalIndex: FractionalIndexCodec
});

// src/presentation/slide-element/utils.ts
import { generate as generate2 } from "@slidebean/fractional-index";
var getElementDefaultData = (elementType) => {
  switch (elementType) {
    case "background" /* Background */: {
      return { image: null, icon: null };
    }
    case "cover" /* Cover */: {
      return { title: "", subtitle: "", author: "" };
    }
    case "heading" /* Heading */: {
      return { heading: "" };
    }
    case "footer" /* Footer */: {
      return { footer: "" };
    }
    case "paragraph" /* Paragraph */: {
      return { paragraph: "" };
    }
    case "bullets" /* Bullets */: {
      return { bullets: [] };
    }
    case "quote" /* Quote */: {
      return { quote: "", author: "" };
    }
    case "image" /* Image */: {
      return { image: null, icon: null };
    }
    case "number" /* Number */: {
      return { number: 0, rawNumber: "", unit: "", caption: "" };
    }
    case "chart-bar" /* ChartBar */: {
      return {
        mode: "vertical",
        stacked: false,
        source: [],
        chart: {
          sets: ["", "", "", ""],
          // 4 sets
          labels: [],
          values: [[], [], [], []],
          title: "",
          labelY: ""
        }
      };
    }
    case "chart-line" /* ChartLine */: {
      return {
        mode: "line",
        stacked: false,
        source: [],
        chart: {
          sets: ["", "", "", ""],
          // 4 sets
          labels: [],
          values: [[], [], [], []],
          title: "",
          labelY: ""
        }
      };
    }
    case "chart-pie" /* ChartPie */: {
      return {
        mode: "pie",
        displayValues: true,
        source: [],
        chart: {
          title: "",
          labels: [],
          values: []
        }
      };
    }
    case "table" /* Table */: {
      return {
        rows: []
      };
    }
    case "code" /* Code */: {
      return { code: "", language: null };
    }
    case "timeline" /* Timeline */: {
      return { items: [], mode: "bottom" };
    }
    case "funnel" /* Funnel */: {
      return { items: [], mode: "funnel" };
    }
    case "venn" /* Venn */: {
      return { groups: 2, values: {} };
    }
    case "line" /* Line */: {
      return {
        points: [
          { x: 0.3, y: 0.5 },
          { x: 0.7, y: 0.5 }
        ]
      };
    }
  }
  return {};
};
var getElementSampleData = (elementType) => {
  switch (elementType) {
    case "cover" /* Cover */: {
      return {
        ...getElementDefaultData(elementType),
        //title: 'This Presentation', subtitle: 'Will Rock', author: 'by Will Rock'
        title: "There and back again",
        subtitle: "A Hobbit's tale",
        author: "by Bilbo Baggins"
      };
    }
    case "heading" /* Heading */: {
      return {
        ...getElementDefaultData(elementType),
        heading: "Heading content goes here"
      };
    }
    case "footer" /* Footer */: {
      return {
        ...getElementDefaultData(elementType),
        footer: "Footer content goes here"
      };
    }
    case "paragraph" /* Paragraph */: {
      return {
        ...getElementDefaultData(elementType),
        paragraph: "Lorem ipsum dolor sit amet"
      };
    }
    case "bullets" /* Bullets */: {
      return {
        ...getElementDefaultData(elementType),
        bullets: ["Bullet point", "Bullet point", "Bullet point"]
      };
    }
    case "quote" /* Quote */: {
      return {
        ...getElementDefaultData(elementType),
        quote: "To be, or not to be, that is the question",
        author: "Prince Hamlet"
      };
    }
    case "image" /* Image */: {
      return {
        ...getElementDefaultData(elementType),
        image: {
          url: "https://slidebean.s3.amazonaws.com/editor/sample-photo.jpg"
        }
      };
    }
    case "number" /* Number */: {
      return {
        ...getElementDefaultData(elementType),
        number: 86400,
        rawNumber: "86,400",
        unit: "seconds",
        caption: "A trip around the sun"
      };
    }
    case "chart-bar" /* ChartBar */: {
      return {
        ...getElementDefaultData(elementType),
        chart: {
          sets: ["Apples", "Oranges", "Pears"],
          labels: ["Alabama", "Vermont", "California"],
          values: [
            [320, 480, 640],
            [400, 600, 300],
            [550, 350, 450]
          ],
          title: ""
        }
      };
    }
    case "chart-line" /* ChartLine */: {
      return {
        ...getElementDefaultData(elementType),
        chart: {
          sets: ["Apples", "Oranges", "Pears"],
          labels: ["Alabama", "Vermont", "California"],
          values: [
            [320, 480, 640],
            [400, 600, 300],
            [550, 350, 450]
          ],
          title: ""
        }
      };
    }
    case "chart-pie" /* ChartPie */: {
      return {
        ...getElementDefaultData(elementType),
        chart: {
          title: "",
          labels: ["Pineapple", "Watermelon", "Mango"],
          values: [30, 40, 60]
        }
      };
    }
    case "table" /* Table */: {
      return {
        ...getElementDefaultData(elementType),
        rows: [
          ["Activity", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          ["Reading", "4", "4", "0", "0", "4"],
          ["Watching TV", "3", "1", "3", "2", "0"],
          ["Exercising", "2", "2", "0", "1", "0"]
        ]
      };
    }
    case "code" /* Code */: {
      return {
        ...getElementDefaultData(elementType),
        code: 'alert("Hello world!");'
      };
    }
    case "timeline" /* Timeline */: {
      return {
        ...getElementDefaultData(elementType),
        items: [
          {
            icon: null,
            image: null,
            text: "<p><b>1955</b></p><p>The Past</p>"
          },
          {
            icon: null,
            image: null,
            text: "<p><b>1985</b></p><p>The Present</p>"
          },
          {
            icon: null,
            image: null,
            text: "<p><b>2015</b></p><p>The Future</p>"
          }
        ]
      };
    }
    case "funnel" /* Funnel */: {
      return {
        ...getElementDefaultData(elementType),
        items: [
          { text: "<p>Prospects</p>" },
          { text: "<p>Contacts</p>" },
          { text: "<p>Leads</p>" },
          { text: "<p>Customers</p>" }
        ]
      };
    }
    case "venn" /* Venn */: {
      return {
        ...getElementDefaultData(elementType),
        values: {
          "a": "<p>A</p>",
          "b": "<p>B</p>",
          "ab": "<p>AB</p>"
        }
      };
    }
    case "line" /* Line */: {
      return {
        ...getElementDefaultData(elementType)
      };
    }
  }
  return {};
};
function createSlideElement({
  id = newId(),
  type: type5,
  data,
  options,
  style,
  fractionalIndex = generate2(1)[0]
}) {
  return throwOnFail(
    SlideElement.decode({
      id,
      type: type5,
      data: recursiveDeepCopy(
        Object.assign({}, getElementDefaultData(type5), data)
      ),
      options: Object.assign({}, options),
      style: clean(style),
      fractionalIndex
    })
  );
}
var getElementOptionValue = (slideElement) => (option) => {
  let value;
  if (slideElement && slideElement.options) {
    value = slideElement.options[option];
  }
  if (typeof value === "undefined") {
    value = {
      "textSize": 0,
      "textAlign": "default",
      "size": 0,
      "imageCrop": "default",
      "xRatio": 0,
      "yRatio": 0,
      "tableFirstRow": true,
      "tableFirstColumn": false
    }[option];
  }
  return value;
};
var isElementDataEmpty = (slideElement) => {
  if (!slideElement.data) {
    return true;
  }
  switch (slideElement.type) {
    case "background" /* Background */: {
      const { image, icon } = slideElement.data;
      return !(image || icon);
    }
    case "cover" /* Cover */: {
      const { title, subtitle, author } = slideElement.data;
      return !(title && title.length) && !(subtitle && subtitle.length) && !(author && author.length);
    }
    case "heading" /* Heading */: {
      const { heading } = slideElement.data;
      return !(heading && heading.length);
    }
    case "footer" /* Footer */: {
      const { footer } = slideElement.data;
      return !(footer && footer.length);
    }
    case "paragraph" /* Paragraph */: {
      const { paragraph } = slideElement.data;
      return !(paragraph && paragraph.length);
    }
    case "bullets" /* Bullets */: {
      const { bullets } = slideElement.data;
      return !Array.isArray(bullets) || !bullets.some((bullet) => bullet.length > 0);
    }
    case "quote" /* Quote */: {
      const { quote, author } = slideElement.data;
      return !(quote && quote.length) && !(author && author.length);
    }
    case "image" /* Image */: {
      const { image, icon } = slideElement.data;
      return !(image || icon);
    }
    case "number" /* Number */: {
      const { number: number2, unit, caption } = slideElement.data;
      return !number2 && !(unit && unit.length) && !(caption && caption.length);
    }
    case "chart-bar" /* ChartBar */:
    case "chart-line" /* ChartLine */:
    case "chart-pie" /* ChartPie */: {
      const { chart } = slideElement.data;
      if (!chart) {
        return true;
      }
      const { labels, values } = chart;
      return !(Array.isArray(labels) && labels.length) && !(Array.isArray(values) && values.length && values.some(
        (rowValues) => !!(Array.isArray(rowValues) && rowValues.length)
      ));
    }
    case "table" /* Table */: {
      const { rows } = slideElement.data;
      return !(Array.isArray(rows) && rows.length && rows.some((cells) => !!(Array.isArray(cells) && cells.length)));
    }
    case "video" /* Video */: {
      const { url, video } = slideElement.data;
      return !(url && url.length) && !video;
    }
    case "code" /* Code */: {
      const { code } = slideElement.data;
      return !(code && code.length);
    }
    case "timeline" /* Timeline */:
    case "funnel" /* Funnel */: {
      const { items } = slideElement.data;
      return !(items && items.length);
    }
    case "venn" /* Venn */: {
      const { values } = slideElement.data;
      return !(values && Object.keys(values).some((key) => !!values[key]));
    }
    case "line" /* Line */: {
      const { points } = slideElement.data;
      return !Array.isArray(points) || points.length === 0;
    }
  }
  return true;
};

// src/presentation/slide.ts
import { generate as generate3 } from "@slidebean/fractional-index";
var Slide = t2.intersection([
  t2.type({
    id: Id,
    elements: t2.array(SlideElement),
    options: t2.any,
    notes: t2.string,
    fractionalIndex: FractionalIndexCodec
  }),
  t2.partial({
    hasBeenManuallyArranged: t2.boolean
  })
]);
function createSlide({
  id = newId(),
  elements,
  options,
  notes = "",
  fractionalIndex = generate3(1)[0],
  hasBeenManuallyArranged
} = {}) {
  return throwOnFail(
    Slide.decode({
      id,
      elements: sortByIndex((elements ?? []).map(createSlideElement)),
      options: Object.assign({}, options),
      notes,
      fractionalIndex,
      hasBeenManuallyArranged: !!hasBeenManuallyArranged
    })
  );
}
var getSlideOptionValue = (slide) => (option, defaults) => {
  const options = slide && slide.options || {};
  const elements = slide && slide.elements || [];
  if (typeof options[option] !== "undefined") {
    return options[option];
  }
  if (option == "figureSize") {
    if (typeof options["imageSize"] !== "undefined")
      return options["imageSize"];
    if (typeof options["iconSize"] !== "undefined")
      return options["iconSize"];
  }
  if (defaults && typeof defaults[option] !== "undefined") {
    return defaults[option];
  }
  return {
    "textSize": 0,
    "colorSet": "default",
    "backgroundDim": typeof options["dimBackground"] !== "undefined" ? options["dimBackground"] ? 0.3 : 0 : elements.length <= 1 ? 0 : 0.3,
    "cropBackground": true,
    "figureSize": 0,
    "chartSize": 0,
    "imageCrop": "default",
    "logoSize": 0,
    "logoPosition": "top-right",
    "bodyVerticalAlign": "top",
    "animate": true
  }[option];
};
function toElementRecord(elements) {
  return arrayToRecord(elements);
}
function toElementArray(elementRecord) {
  return recordToArray(elementRecord, compareIndexes);
}
function toWritableSlide(slide) {
  return {
    ...slide,
    elements: toElementRecord(slide.elements)
  };
}
function fromWritableSlide(writableSlide) {
  return throwOnFail(
    Slide.decode({
      ...writableSlide,
      elements: toElementArray(writableSlide.elements)
    })
  );
}

// src/presentation/presentation-document.ts
import * as t3 from "io-ts";
var PresentationDocument = t3.intersection([
  Entity,
  t3.type({
    name: Name,
    summary: t3.string,
    evoMode: t3.boolean,
    slides: t3.array(Slide),
    createdAt: DateCodec,
    editedAt: t3.readonly(DateCodec)
  }),
  t3.partial({
    formulaId: t3.union([Id, t3.null]),
    layoutId: t3.union([Id, t3.null]),
    paletteId: t3.union([Id, t3.null]),
    textFontId: t3.union([Id, t3.null]),
    headingFontId: t3.union([Id, t3.null]),
    logoLightBg: t3.union([Url, t3.null]),
    logoDarkBg: t3.union([Url, t3.null])
  })
]);
function toWritableSlideRecord(slides) {
  return arrayToRecord(slides.map(toWritableSlide));
}
function toSlideArray(writableSlideRecord) {
  return recordToArray(writableSlideRecord, compareIndexes).map(
    fromWritableSlide
  );
}
function toWritablePresentationDocument(doc) {
  return {
    ...doc,
    slides: toWritableSlideRecord(doc.slides)
  };
}
function fromWritablePresentationDocument(writableDoc) {
  return throwOnFail(
    PresentationDocument.decode({
      ...writableDoc,
      slides: toSlideArray(writableDoc.slides)
    })
  );
}

// src/presentation/change-proposal.ts
import * as t4 from "io-ts";
import { applyPatches, enablePatches } from "immer";
enablePatches();
function isPatch(input) {
  return !!input && ["replace", "remove", "add"].includes(input.op) && Array.isArray(input.path) && input.path.length > 0;
}
var PatchCodec = fromRefinement("Patch", isPatch);
var ChangeProposal = t4.type({
  id: Id,
  patches: t4.array(PatchCodec)
});
function applyPatchesToDocument({
  patches,
  document
}) {
  return applyPatches(document, patches);
}
export {
  ChangeProposal,
  FractionalIndexCodec,
  PatchCodec,
  PresentationDocument,
  Slide,
  SlideElement,
  SlideElementStyle,
  SlideElementType,
  SlideElementTypeCodec,
  applyPatchesToDocument,
  compareIndexes,
  compareIndexesAndIds,
  createSlide,
  createSlideElement,
  fromWritablePresentationDocument,
  fromWritableSlide,
  getElementDefaultData,
  getElementOptionValue,
  getElementSampleData,
  getSlideOptionValue,
  hasDuplicatedIndexes,
  isElementDataEmpty,
  sortByIndex,
  toElementArray,
  toElementRecord,
  toSlideArray,
  toWritablePresentationDocument,
  toWritableSlide,
  toWritableSlideRecord
};
